import {useTranslation} from "react-i18next";
import EntityCRUDTable from "./EntityCRUDTable";
import React, {useState, useEffect, useMemo} from "react";
import TextField from "@mui/material/TextField";
import {reportCRUD, questionnaireCRUD} from "../../../http";
import {MenuItem, Button, Chip, Autocomplete, Alert, CircularProgress} from "@mui/material";
import {getFilials, getFilialDivisions, getDepartments, getEmployees} from "../../../redux/actions";
import {FilialSelect, FilialDivisionSelect} from "../../Select/domainSelects";
import {Box, Typography, Paper} from "@mui/material";
import {useSelector, useDispatch} from "react-redux";
import ReportPdfModel from "../../../models/reportPdfModel";

export default function ReportCRUDTable({...props}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const filials = useSelector(state => state.filial?.filials || []);
    const departments = useSelector(state => state.department?.departments || []);
    const employees = useSelector(state => state.employee?.employees || []);
    
    const defaultNpaLink = `Закон О биологической безопасности Республики Казахстан от 21 мая 2022 года № 122-VII ЗРК.
- Санитарные правила «Об утверждении правил обеспечения биологической защиты», приказ МЗ РК от 2 ноября 2022 года № ҚР ДСМ-125. 
- Санитарные правила «Санитарно-эпидемиологические требования к объектам здравоохранения», приказ Министерства здравоохранения РК №96 от 11.08.2020 г.
- Санитарные правила «Санитарно-эпидемиологические требования к сбору, использованию, применению, обезвреживанию, транспортировке, хранению и захоронению отходов производства и потребления», приказ МЗ РК от 25 декабря 2020 года № ҚР ДСМ-331/2020.
- Приказ МЗ РК от 15.10.2020г. № ҚР ДСМ-131/2020 «Об утверждении целевых групп лиц, подлежащих обязательным медицинским осмотрам, а также правил и периодичности их проведения, объема лабораторных и функциональных исследований, …»

- Приказ МЗ РК №758 от 28.09.2015 г. об утверждении «Положения о деятельности организаций и (или) структурных подразделений организаций здравоохранения, осуществляющих лабораторную диагностику, а также объем и виды проводимых ими исследований». 
- Рекомендации всемирной организации здравоохранения (ВОЗ)`;

    const defaultAbbreviations = `РГП на ПХВ – Республиканское государственное предприятие на правах хозяйственного ведения;
КСЭК – Комитет санитарно-эпидемиологического контроля;
МЗ – Министерство здравоохранения;
РК – Республика Казахстан;
НЦЭ-Национальный центр экспертизы;
CDC – Центры по контролю и профилактике;
ООИ – особо опасные инфекции;
ЦА – центральный аппарат;
ББ-биобезопасность;
БЗ-биозащита;
ШББ- шкаф биологической безопасности;
ПБА - патогенные биологические агенты;
НПА-нормативно-правовые акты;
СИЗ – средства индивидуальной защиты;
СОП- стандартная операционная процедура;
СМК –система менеджмента качества;
IQLS - Integrated Quality Laboratory Services (Инструмент аудита безопасности в лаборатории);
МЛ – Медицинская лаборатория;
СТ РК – Национальный стандарт;
ДП – документированная процедура;
ВЛ – вирусологическая лаборатория;
БЛ – бактериологическая лаборатория;
ТБ – техника безопасности;
ИЦ – испытательная лаборатория;
СИ – средство измерения;
ИО – испытательное оборудование.`;

    const [entity, setEntity] = useState({
        createdAt: new Date().toISOString(),
        assessmentDate: new Date().toISOString(),
        npaLink: defaultNpaLink,
        abbreviations: defaultAbbreviations,
        introduction: null,
        generalInformation: null,
        toolsDescription: null,
        results: null,
        conclusion: null,
        recommendations: null,
        laboratories: null,
        experts: null,
        filialId: null,
        filialDivisionId: null,
        questionnaireIds: null // Массив ID опросов
    });

    const [availableQuestionnaires, setAvailableQuestionnaires] = useState([]);
    const [loadingQuestionnaires, setLoadingQuestionnaires] = useState(false);
    const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
    const [selectedExperts, setSelectedExperts] = useState([]);
    const [pdfDataUrl, setPdfDataUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const generatePdfPreview = async (report) => {
        try {
            setPdfLoading(true);
            const model = new ReportPdfModel();
            // Подмешиваем название филиала, чтобы в PDF не было "по ID: N"
            const filialNameForPdf = report?.filial?.name || (report?.filialId ? getFilialName(report.filialId) : null);
            const filialRegionLabel = filialNameForPdf ? getFilialRegionLabel(filialNameForPdf) : null;
            let laboratoriesText = report?.laboratories;

            const getLabNameFromQuestionnaire = (q) => {
                try {
                    const value = typeof q.value === "string" ? JSON.parse(q.value) : q.value;
                    if (value && value.generalInfo) {
                        const departmentId = value.generalInfo.departmentId;
                        const departmentName =
                            value.generalInfo.departmentName ||
                            value.generalInfo.laboratoryName ||
                            (departmentId
                                ? (departments.find(d => Number(d.id) === Number(departmentId))?.name || null)
                                : null);
                        return departmentName;
                    }
                    return null;
                } catch (e) {
                    return null;
                }
            };

            // Если лаборатории не заданы вручную, формируем список из выбранных опросников
            if (!laboratoriesText && selectedQuestionnaires.length > 0) {
                const labLines = selectedQuestionnaires.map((q) => {
                    try {
                        const deptName = getLabNameFromQuestionnaire(q);
                        if (deptName) return String(deptName).trim();
                        return null;
                    } catch (e) {
                        return null;
                    }
                });
                // Убираем пустые и дубли, выводим только названия лабораторий
                const uniq = Array.from(new Set(labLines.filter(Boolean)));
                laboratoriesText = uniq.join("\n");
            }

            // Формируем текст экспертов из выбранных экспертов или из JSON
            let expertsText = report?.experts;
            if (!expertsText && selectedExperts.length > 0) {
                const expertNames = selectedExperts
                    .map(exp => exp.fullName || exp.name || `ID: ${exp.id}`)
                    .filter(Boolean);
                expertsText = expertNames.join("\n");
            } else if (expertsText && typeof expertsText === 'object') {
                // Если experts - это JSON объект/массив, преобразуем в текст
                if (Array.isArray(expertsText)) {
                    expertsText = expertsText
                        .map(exp => exp.name || exp.fullName || `ID: ${exp.id}`)
                        .filter(Boolean)
                        .join("\n");
                } else {
                    expertsText = String(expertsText);
                }
            }

            const reportForPdf = {
                ...report,
                filial: filialNameForPdf ? {...(report?.filial || {}), name: filialNameForPdf} : report?.filial,
                filialRegionLabel,
                laboratories: laboratoriesText,
                experts: expertsText
            };

            const dataUrl = await model.getDataUrl(reportForPdf);
            setPdfDataUrl(dataUrl);
        } catch (e) {
            console.error("Ошибка генерации PDF:", e);
            // Не показываем alert, чтобы не мешать редактированию; ошибка видна в консоли
            setPdfDataUrl(null);
        } finally {
            setPdfLoading(false);
        }
    };

    // Автопревью PDF как в протоколах: пересобираем с debounce при изменении данных отчета.
    useEffect(() => {
        // Не спамим генерацией, если вообще нет данных
        if (!entity) return;

        const t = setTimeout(() => {
            generatePdfPreview(entity);
        }, 450);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        entity?.id,
        entity?.createdAt,
        entity?.assessmentDate,
        entity?.filialId,
        entity?.filialDivisionId,
        entity?.npaLink,
        entity?.abbreviations,
        entity?.introduction,
        entity?.generalInformation,
        entity?.toolsDescription,
        entity?.results,
        entity?.conclusion,
        entity?.recommendations,
        entity?.laboratories,
        entity?.experts,
        entity?.questionnaireIds,
        selectedQuestionnaires, // Добавляем зависимость от выбранных опросников, чтобы лаборатории обновлялись в PDF
        selectedExperts, // Добавляем зависимость от выбранных экспертов, чтобы эксперты обновлялись в PDF
    ]);

    // Загрузка филиалов при монтировании компонента
    useEffect(() => {
        dispatch(getFilials({paging: {skip: 0}}));
    }, [dispatch]);

    // Загрузка лабораторий (departments) для отображения названий вместо ID
    useEffect(() => {
        dispatch(getDepartments({paging: {skip: 0}}));
    }, [dispatch]);

    // Загрузка сотрудников (employees) для выбора экспертов
    useEffect(() => {
        dispatch(getEmployees({paging: {skip: 0}}));
    }, [dispatch]);

    // Функция для получения названия филиала
    const getFilialName = (filialId) => {
        if (!filialId) return null;
        // Ищем филиал в Redux store
        const filial = filials.find(f => Number(f.id) === Number(filialId));
        // Если филиал найден, возвращаем его название, иначе используем ID как fallback
        // Это позволяет генерировать текст даже если филиалы еще не загружены
        return filial?.name || `ID: ${filialId}`;
    };

    // Нормализация: если в названии филиала уже есть "Филиал ... по <области/городу>",
    // то для шаблонов вида "по области X" нужно подставлять только X (без повторов).
    const getFilialRegionLabel = (filialName) => {
        if (!filialName) return null;
        if (/^Филиал\b/i.test(filialName)) {
            // Берем часть после последнего " по " (обычно это "Алматинской области" и т.п.)
            const parts = filialName.split(/\s+по\s+/i);
            const tail = parts[parts.length - 1]?.trim();
            return tail || filialName;
        }
        return filialName;
    };

    // Функция для генерации текста введения
    const generateIntroductionText = (filialId, assessmentDate) => {
        if (!filialId || !assessmentDate) {
            return null;
        }

        const filialName = getFilialName(filialId);
        const filialRegionLabel = getFilialRegionLabel(filialName);
        // Используем ID как fallback, если название не найдено
        if (!filialName) {
            return null;
        }

        // Форматируем дату оценки
        let formattedDate = "";
        try {
            const date = new Date(assessmentDate);
            const day = date.getDate();
            const monthNames = [
                "января", "февраля", "марта", "апреля", "мая", "июня",
                "июля", "августа", "сентября", "октября", "ноября", "декабря"
            ];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            formattedDate = `${day} ${month} ${year} г.`;
        } catch (e) {
            formattedDate = new Date(assessmentDate).toLocaleDateString('ru-RU');
        }

        return `Основная цель оценки биобезопасности лабораторий филиала «Национального центра экспертизы» (далее – НЦЭ) по области ${filialRegionLabel} – профилактика воздействия патогенных микроогранизмов на персонал лаборатории и объектов окружающей среды, выявление, анализ несоответствий и рекомендации по их устранению, направленные на непрерывное совершенствование соблюдений требований биобезопасности в соответствии с нормативно регуляторными документами Республики Казахстан.
${formattedDate} проведена оценка биобезопасности и биозащиты микробиологических лабораторий НЦЭ по области ${filialRegionLabel} (далее Филиал): 
1.	
2.	
Перед началом оценки было проведено вступительное совещание в присутствии директора, специалиста по биобезопасности филиала, менеджера по качеству,  заведующих лабораториями. На совещании было дано разъяснение о цели оценки и согласованы сроки и время ее проведения. В ходе оценки были посещены бактериологическая лаборатория; вирусологическая лаборатория с мобильным лабораторным комплексом; а так же новая лаборатория особо опасных инфекций, которая на момент проверки еще не функционировала.
Были назначены сопровождающие лица от филиала РГП на ПХВ «Национальный центр экспертизы» КСЭК МЗ РК по области ${filialRegionLabel}, которые сопровождали группу во время проведения оценки.
Было дано разъяснение, в какие сроки и в каком виде будет предоставлен отчет по оценке и какие последующие действия должны быть выполнены со стороны филиала.
В лабораторных помещениях было осмотрены места работы персонала, оборудование, средства измерения, условия окружающей среды и порядок использования и хранения реагентов и расходных материалов, рассмотрены процедуры СМК, документированные процедуры и записи. У специалиста по биобезопасности были рассмотрены процедуры, СОП, руководство.`;
    };

    // Функция для генерации текста общей информации
    const generateGeneralInformationText = (filialId) => {
        if (!filialId) {
            return null;
        }

        const filialName = getFilialName(filialId);
        const filialRegionLabel = getFilialRegionLabel(filialName);
        // Используем ID как fallback, если название не найдено
        if (!filialName) {
            return null;
        }

        const filialFullLabel = /^Филиал\b/i.test(filialName)
            ? filialName
            : `Филиал РГП на ПХВ «Национальный центр экспертизы» КСЭК МЗ РК по области ${filialRegionLabel}`;

        return `ОБЩАЯ ИНФОРМАЦИЯ ПО ЛАБОРАТОРИЯМ
${filialFullLabel} расположен по адресу ул. Гурбы 6, на территории расположено 3 здания. В трехэтажном здании на первом этаже расположены складские помещения, административные кабинеты, а правое крыло занимает вирусологическая лаборатория. В этом же здании на втором этаже расположены административные кабинеты, часть из которых занимает городское управление СЭК, на третьем этаже расположены лаборатории санитарно-гигиеническая и ВТИ. Бактериологическая лаборатория размещена в отдельно стоящем двухэтажном здании на первом этаже, на втором этаже этого же здания сидит городское управление СЭК.   
Лаборатория особо опасных инфекций (общая площадь – 135,48 м2) расположена в одноэтажном здании, где так же располагается паразитологический отдел бактериологической лаборатории, дезинфекционный отдел, складское помещение, электрощитовая, помещение где находится отопительная система. 
Территория здания имеет отдельное ограждение, видеонаблюдение. Подъездные пути асфальтированы частично.  Для временного хранения отходов класса А есть контейнеры -2 шт, площадка небетонированная, нет крышки, навеса. Вывоз мусора проводит специализированная организация  по договору № 243 от 16.03.2023г. с «Сат тазалык». Вывоз медицинских отходов класса Б, В проводится на основании заключенного договора договор с ТОО "EcoStroiService" №146  от 23.09.24 г. класс В- 500 кг, класс Б- 250 кг.  
 Здание подключено к водоснабжению и канализации, и центральному отоплению. В лабораториях имеется естественное и искусственное освещение, оснащены пожарной сигнализацией, средствами пожаротушения, имеются схемы эвакуации. Медицинский осмотр договор №158 от 30.09.2024 г на сумму 609 345,28. Мед осмотр лабораторий допуск до января 2025 года. Анализ на ВИЧ по медицинскому осмотру прошло более полугода. 
С апреля 2023 года Филиал стал областным центром. В связи с этим лаборатория ООИ находится на стадии открытия. На момент оценки штатная структура лаборатории ООИ утверждена: 1 заведующая, 1 зоолог, 2 лаборантов, 1 санитарка. Сертификационный курс (первичная специализация) для работы со 2 группой патогенности имеется у заведующей лаборатории, зоолога, 1 лаборанта. Лаборатория имеет разграничение на «чистую» и «грязную» зоны. Набор помещений в «чистой» зоне: рабочий кабинет, гардероб, санпропускник, санузел, склад, бокс для приготовления и розлива сред, препараторская, моечная. Набор помещений в «грязной» зоне: автоклавная, 2 боксированных помещения для бактериологических исследований, санпропускник, боксы для 1 и 2 этапа ПЦР (вместе с 3 этапом амплификации), комната для постановки ИФА и серологических реакций, бокс для заражения и вскрытия биопробных животных, комната для приема проб, санузел. В лаборатории на окнах отсутствуют решетки, москитные сетки. Боксированные помещения не имеют предбоксников, в грязной зоне имеются 2 боксированных помещения вход, в которые осуществляется через другие боксированные помещения, что недопустимо.

**Замечания по СМК:**
Аудит основан на выборочной проверке информации, представленной персоналом лаборатории бактериологической, вирусологической, лаборатория ООИ, санитарно-гигиеническая лаборатория, радиологическая лаборатория, лаборатория высокотехнологических исследований. В ИЦ проводятся исследования по государственному заказу и платных услуг.`;
    };

    // Функция для генерации текста описания инструментов
    const generateToolsDescriptionText = (filialId) => {
        if (!filialId) {
            return null;
        }

        const filialName = getFilialName(filialId);
        // Используем ID как fallback, если название не найдено
        if (!filialName) {
            return null;
        }

        return `ОПИСАНИЕ ИНСТРУМЕНТОВ ОЦЕНКИ ЛАБОРАТОРИЙ
Оценка лабораторий филиала РГП на ПХВ «НЦЭ» КСЭК МЗ РК по области ${filialName} проведена в соответствии с инструментом разработанный IQLS (США), 2019 г. (Комплексное качество лабораторных услуг) (www.igls.net) и адаптирован в соответствии национальными нормативными документами представленный в виде файла в формате Excel. Вопросник содержит индикаторы по 9 разделам:
1)	Помещения и рабочие потоки.
В модуле оцениваются состояние и оснащение лабораторных помещений и условия работы. Рабочие помещения и инфраструктура лаборатории должны быть спланированы так, чтобы рабочая нагрузка не сказывалась отрицательно на качестве работы и безопасности сотрудников. Персонал лаборатории должен иметь достаточное пространство и безопасные условия для выполнения испытаний. Следует обеспечить адекватные условия освещения, вентиляции, водоснабжения. 
2)	Обучение и управление персоналом. 
В МЛ персонал задействованный в лабораторном процессе должен обладать компетентностью, пройти профессиональную подготовку, по назначению использовать лабораторные методы и оборудования, правильно использовать СИЗ, проходить непрерывное обучение, в том числе по вопросам оценки рисков и ликвидации рисков и их последствий. 
3)	Правила лабораторной практики. 
В лаборатории должны быть использованы надлежащие методы и процедуры микробиологической работы. Должны соблюдаться требования по зонированию помещений, исключения перекреста ПБА и сотрудников, созданы условия по соблюдению гигиены рук, предотвращения распространения ПБА, условия для применения надлежащих методов обеззараживания и дезактивации/утилизации материалов, персонал должен быть в достаточном количестве обеспечен СИЗ и др.
4)	Уборка, обеззараживание, стерилизация и управление отходами.
Уборка, обеззараживание, обращение с МО является комплексным мероприятием, нацеленным на профилактику инфекций и инфекционный контроль, требующим многопланового подхода, который может охватывать проведение инструктажа, мониторинга, контроля, получение обратной связи, распространение памяток и наглядного материала в отношении СОП по ряду важных направлений.
5)	Экстренные ситуации. 
Во время работы в медицинских лабораториях могут возникнуть следующие аварии и аварийные ситуации: пожар, взрыв, поражение электрическим током, попадание кислоты или щелочи на кожу или в глаза, выделение или образование едких, ядовитых, огне- или взрывоопасных веществ. Оценка и проведение предупреждающих мероприятий создает реальную или потенциальную возможность предотвратить выделение ПБА в воздух производственной зоны, среду обитания человека и заражения персонала.   
6)	Управление рисками.
Оценка модуля управление рисками позволит провести систематический процесс сбора информации, оценки вероятности и масштаба последствий воздействия или утечки опасных факторов на рабочем месте, а также определения соответствующих мер контроля для снижения уровня рисков, кроме этого, позволит предоставить в стандартном формате информацию об административно-организационной структуре, выявить сильные и слабые стороны связанных с охраной здоровья персонала лаборатории, повысить информированность, снабдить объективными данными для планирования, разработки стратегии и осуществления деятельности по снижению риска от каждого источника опасности до минимально возможного уровня.   
7)	Документация и нормативные положения.
В Медицинских лабораториях, аккредитованных в системе менеджмента качества, должна работать система управления документацией. Лаборатории должны иметь разработанные документированные процедуры на все лабораторные процессы.  
8)	Биозащита.
Модуль позволяет оценить обеспечение защиты, контроля и учета ПБА в лаборатории, разработать мероприятия по предотвращению их утери, утечки, кражи, неправильного использования. 
9) Прочие риски.
Модуль позволяет выявить и идентифицировать прочие риски, связанные с лабораторной деятельностью. Разработать мероприятия по устранению либо минимизации этих рисков.
Критерии индикаторов по программе IQLS показывают уровень биобезопасности в лаборатории и необходимость принятия определённых мер:
- показатель ниже 50% – необходимы значительные улучшения; 
- показатель от 50% до 80% – необходимы некоторые улучшения; 
- показатель выше 80% – в лаборатории хорошая ситуация.`;
    };

    // Автоматическое обновление текста введения при изменении филиала или даты оценки
    useEffect(() => {
        // Обновляем текст введения только если:
        // 1. Это новый отчет (нет ID)
        // 2. Указаны филиал и дата оценки
        // 3. Поле введения пустое или null
        if (!entity.id && entity.filialId && entity.assessmentDate) {
            const newIntroduction = generateIntroductionText(entity.filialId, entity.assessmentDate);
            if (newIntroduction) {
                const currentIntro = entity.introduction || "";
                if (!currentIntro || currentIntro.trim() === "") {
                    setEntity((prevEntity) => ({...prevEntity, introduction: newIntroduction}));
                }
            }
        }
    }, [entity.filialId, entity.assessmentDate, entity.id]);

    // Автоматическое обновление текста общей информации при изменении филиала
    useEffect(() => {
        // Обновляем текст общей информации только если:
        // 1. Это новый отчет (нет ID)
        // 2. Указан филиал
        // 3. Поле общей информации пустое или null
        if (!entity.id && entity.filialId) {
            const newGeneralInformation = generateGeneralInformationText(entity.filialId);
            if (newGeneralInformation) {
                const currentGenInfo = entity.generalInformation || "";
                if (!currentGenInfo || currentGenInfo.trim() === "") {
                    setEntity((prevEntity) => ({...prevEntity, generalInformation: newGeneralInformation}));
                }
            }
        }
    }, [entity.filialId, entity.id]);

    // Автоматическое обновление текста описания инструментов при изменении филиала
    useEffect(() => {
        // Обновляем текст описания инструментов только если:
        // 1. Это новый отчет (нет ID)
        // 2. Указан филиал
        // 3. Поле описания инструментов пустое или null
        if (!entity.id && entity.filialId) {
            const newToolsDescription = generateToolsDescriptionText(entity.filialId);
            if (newToolsDescription) {
                const currentToolsDesc = entity.toolsDescription || "";
                if (!currentToolsDesc || currentToolsDesc.trim() === "") {
                    setEntity((prevEntity) => ({...prevEntity, toolsDescription: newToolsDescription}));
                }
            }
        }
    }, [entity.filialId, entity.id]);

    const setValue = (key, value) => {
        setEntity((prevEntity) => ({...prevEntity, [key]: value}));
    };

    const clearEntity = () => {
        setEntity({
            createdAt: new Date().toISOString(),
            assessmentDate: new Date().toISOString(),
            npaLink: defaultNpaLink,
            abbreviations: defaultAbbreviations,
            introduction: null,
            generalInformation: null,
            toolsDescription: null,
            results: null,
            conclusion: null,
            recommendations: null,
            laboratories: null,
            experts: null,
            filialId: null,
            filialDivisionId: null,
            questionnaireIds: null
        });
        setSelectedQuestionnaires([]);
        setAvailableQuestionnaires([]);
    };

    // Загрузка доступных опросов по параметрам отчета
    const loadQuestionnairesByFilters = async () => {
        if (!entity.assessmentDate || !entity.filialId) {
            return;
        }

        setLoadingQuestionnaires(true);
        try {
            const assessmentYear = new Date(entity.assessmentDate).getFullYear();
            
            // Загружаем все опросы без фильтра по дате на уровне API
            // Т.к. дата может быть в value.generalInfo.date, а не только в createdAt
            const query = {
                paging: {skip: 0, take: 10000} // Увеличиваем лимит
            };

            const response = await questionnaireCRUD.search(query);
            let questionnaires = response.result || [];

            // Фильтруем по году и филиалу из value.generalInfo
            questionnaires = questionnaires.filter(q => {
                try {
                    const value = typeof q.value === 'string' ? JSON.parse(q.value) : q.value;
                    
                    // Проверяем год из value.generalInfo.date или createdAt
                    let questionnaireYear = null;
                    if (value && value.generalInfo && value.generalInfo.date) {
                        try {
                            questionnaireYear = new Date(value.generalInfo.date).getFullYear();
                        } catch (e) {
                            // Если не удалось распарсить дату из generalInfo, используем createdAt
                            if (q.createdAt) {
                                questionnaireYear = new Date(q.createdAt).getFullYear();
                            }
                        }
                    } else if (q.createdAt) {
                        questionnaireYear = new Date(q.createdAt).getFullYear();
                    }
                    
                    // Проверяем год
                    if (questionnaireYear !== assessmentYear) {
                        return false;
                    }
                    
                    // Проверяем филиал
                    let filialId = null;
                    if (value && value.generalInfo && value.generalInfo.filialId) {
                        filialId = value.generalInfo.filialId;
                    } else if (q.filialId) {
                        filialId = q.filialId;
                    }
                    
                    if (filialId && Number(filialId) === Number(entity.filialId)) {
                        return true;
                    }
                    
                    return false;
                } catch (e) {
                    console.error("Ошибка фильтрации опроса:", e, q);
                    return false;
                }
            });

            // Если указано подразделение филиала, фильтруем дополнительно
            if (entity.filialDivisionId) {
                questionnaires = questionnaires.filter(q => {
                    try {
                        const value = typeof q.value === 'string' ? JSON.parse(q.value) : q.value;
                        if (value && value.generalInfo && value.generalInfo.filialDivisionId) {
                            return Number(value.generalInfo.filialDivisionId) === Number(entity.filialDivisionId);
                        }
                        if (q.filialDivisionId) {
                            return Number(q.filialDivisionId) === Number(entity.filialDivisionId);
                        }
                        return true; // Если подразделение не указано в опросе, включаем его
                    } catch (e) {
                        return true;
                    }
                });
            }

            console.log(`Найдено опросов: ${questionnaires.length} для филиала ${entity.filialId} за ${assessmentYear} год`);
            
            // Сохраняем текущие выбранные опросы, которые все еще есть в новых результатах
            const currentSelectedIds = new Set(selectedQuestionnaires.map(q => Number(q.id)));
            const preservedSelected = selectedQuestionnaires.filter(q => 
                questionnaires.some(nq => Number(nq.id) === Number(q.id))
            );
            
            // Добавляем новые опросы, которых еще нет в выбранных
            const newQuestionnaires = questionnaires.filter(nq => 
                !currentSelectedIds.has(Number(nq.id))
            );
            
            setAvailableQuestionnaires(questionnaires);
            
            // Объединяем сохраненные выбранные опросы с новыми (если нужно)
            // НЕ перезаписываем выбранные опросы автоматически
            // Пользователь сам выберет нужные опросы из списка
        } catch (error) {
            console.error("Ошибка загрузки опросов:", error);
            alert(`Ошибка загрузки опросов: ${error.message || error}`);
        } finally {
            setLoadingQuestionnaires(false);
        }
    };

    // Загрузка выбранных опросов ТОЛЬКО при редактировании (когда есть entity.id).
    // В режиме создания не трогаем selectedQuestionnaires, иначе выбор будет сбрасываться.
    useEffect(() => {
        if (!entity.id) return;

        const loadSelectedQuestionnaires = async () => {
            if (entity.questionnaireIds && Array.isArray(entity.questionnaireIds) && entity.questionnaireIds.length > 0) {
                try {
                    const questionnaires = [];
                    for (const id of entity.questionnaireIds) {
                        try {
                            const q = await questionnaireCRUD.get(id);
                            if (q) questionnaires.push(q);
                        } catch (e) {
                            console.error(`Ошибка загрузки опроса ${id}:`, e);
                        }
                    }
                    setSelectedQuestionnaires(questionnaires);
                } catch (error) {
                    console.error("Ошибка загрузки выбранных опросов:", error);
                }
            } else {
                setSelectedQuestionnaires([]);
            }
        };

        loadSelectedQuestionnaires();
    }, [entity.id, entity.questionnaireIds]);

    // Загрузка выбранных экспертов из JSON при редактировании
    useEffect(() => {
        const loadSelectedExperts = async () => {
            if (entity.id && entity.experts) {
                try {
                    // Парсим JSON экспертов
                    const expertsData = typeof entity.experts === 'string' 
                        ? JSON.parse(entity.experts) 
                        : entity.experts;
                    
                    if (Array.isArray(expertsData) && expertsData.length > 0) {
                        // Ищем сотрудников по ID из JSON
                        const loadedExperts = expertsData
                            .map(expert => {
                                if (expert.id) {
                                    const employee = employees.find(e => Number(e.id) === Number(expert.id));
                                    if (employee) {
                                        return employee;
                                    }
                                }
                                // Если сотрудник не найден, создаем объект из JSON данных
                                return expert;
                            })
                            .filter(Boolean);
                        setSelectedExperts(loadedExperts);
                    } else {
                        setSelectedExperts([]);
                    }
                } catch (error) {
                    console.error("Ошибка загрузки выбранных экспертов:", error);
                    setSelectedExperts([]);
                }
            } else {
                setSelectedExperts([]);
            }
        };

        loadSelectedExperts();
    }, [entity.id, entity.experts, employees]);

    // Получение названия опроса для отображения
    const getQuestionnaireLabel = (questionnaire) => {
        try {
            const value = typeof questionnaire.value === 'string' ? JSON.parse(questionnaire.value) : questionnaire.value;
            if (value && value.generalInfo) {
                const date = value.generalInfo.date ? new Date(value.generalInfo.date).toLocaleDateString('ru-RU') : '';
                const departmentId = value.generalInfo.departmentId;
                const departmentName =
                    value.generalInfo.departmentName ||
                    value.generalInfo.laboratoryName ||
                    (departmentId
                        ? (departments.find(d => Number(d.id) === Number(departmentId))?.name || null)
                        : null);
                return `Опрос ${date}${departmentName ? ` (${departmentName})` : ''}`;
            }
        } catch (e) {
            // ignore
        }
        return `Опрос ID: ${questionnaire.id}`;
    };

    // Форматирование даты для отображения
    const formatDate = (dateString) => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toISOString().slice(0, 16); // Формат для datetime-local
        } catch (e) {
            return dateString;
        }
    };

    // Форматирование даты ISO для отправки
    const formatDateISO = (dateString) => {
        if (!dateString) return null;
        try {
            return new Date(dateString).toISOString();
        } catch (e) {
            return dateString;
        }
    };

    // Форматирование JSON для отображения
    const formatJSON = (json) => {
        if (!json) return "";
        if (typeof json === 'string') return json;
        return JSON.stringify(json, null, 2);
    };

    // Парсинг JSON из строки
    const parseJSON = (jsonString) => {
        if (!jsonString || jsonString.trim() === "") return null;
        try {
            return JSON.parse(jsonString);
        } catch (e) {
            return null;
        }
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id"
        },
        {
            title: "Дата оценки",
            dataIndex: "assessmentDate",
            key: "assessmentDate",
            render: (value) => {
                if (!value) return "-";
                try {
                    return new Date(value).toLocaleDateString('ru-RU');
                } catch (e) {
                    return String(value);
                }
            }
        },
        {
            title: "Филиал",
            dataIndex: "filial",
            key: "filialName",
            render: (_, row) => {
                if (row.filial?.name) return row.filial.name;
                if (row.filialId) return `ID: ${row.filialId}`;
                return "-";
            }
        },
        {
            title: "Подразделение филиала",
            dataIndex: "filialDivision",
            key: "filialDivisionName",
            render: (_, row) => {
                if (row.filialDivision?.name) return row.filialDivision.name;
                if (row.filialDivisionId) return `ID: ${row.filialDivisionId}`;
                return "-";
            }
        },
        {
            title: "Дата создания",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value) => {
                if (!value) return "-";
                try {
                    return new Date(value).toLocaleString('ru-RU');
                } catch (e) {
                    return String(value);
                }
            }
        }
    ];

    const filterFields = [
        {
            title: "Дата оценки (от)",
            key: "assessmentDate",
            operandPosition: 1,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Дата оценки (до)",
            key: "assessmentDate",
            operandPosition: 2,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filial",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Подразделение филиала",
            key: "filialDivisionId",
            compareType: "equals",
            reducerKey: "filialDivision",
            reducerArrayKey: "filialDivisions",
            reducerAction: getFilialDivisions,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        }
    ];

    // Убеждаемся, что createdAt устанавливается при создании
    useEffect(() => {
        if (!entity.id && !entity.createdAt) {
            setEntity({...entity, createdAt: new Date().toISOString()});
        }
    }, []);

    // Объединяем доступные и выбранные опросы, чтобы выбранные не исчезали
    // Важно: сохраняем ссылки на объекты из selectedQuestionnaires, чтобы Autocomplete мог их правильно сопоставить
    const allQuestionnaireOptions = useMemo(() => {
        // Создаем Map для быстрого поиска по ID
        const optionsMap = new Map();
        
        // Сначала добавляем все доступные опросы
        availableQuestionnaires.forEach(q => {
            optionsMap.set(Number(q.id), q);
        });
        
        // Затем добавляем выбранные опросы (они имеют приоритет, если есть дубликаты)
        selectedQuestionnaires.forEach(q => {
            optionsMap.set(Number(q.id), q);
        });
        
        // Возвращаем массив, сохраняя порядок: сначала доступные, потом выбранные
        const result = [...availableQuestionnaires];
        selectedQuestionnaires.forEach(selected => {
            if (!result.some(q => Number(q.id) === Number(selected.id))) {
                result.push(selected);
            }
        });
        
        return result;
    }, [availableQuestionnaires, selectedQuestionnaires]);

    const createFields = useMemo(() => [
        <TextField
            key="assessmentDate"
            margin="normal"
            type="datetime-local"
            value={formatDate(entity.assessmentDate)}
            onChange={(e) => setValue("assessmentDate", formatDateISO(e.target.value))}
            fullWidth
            id="assessmentDate"
            name="assessmentDate"
            label="Дата оценки *"
            InputLabelProps={{shrink: true}}
            required
        />,
        <FilialSelect
            key="filialId"
            value={entity.filialId}
            onChange={(value) => {
                setEntity((prevEntity) => {
                    const newEntity = {...prevEntity, filialId: value};
                    // Очищаем filialDivisionId при смене филиала
                    if (value !== prevEntity.filialId) {
                        newEntity.filialDivisionId = null;
                    }
                    return newEntity;
                });
            }}
            fullWidth
            margin="normal"
        />,
        <FilialDivisionSelect
            key="filialDivisionId"
            filialId={entity.filialId}
            value={entity.filialDivisionId}
            onChange={(value) => setValue("filialDivisionId", value)}
            fullWidth
            margin="normal"
        />,
        <TextField
            key="npaLink"
            margin="normal"
            value={entity.npaLink || defaultNpaLink}
            onChange={(e) => setValue("npaLink", e.target.value || defaultNpaLink)}
            fullWidth
            id="npaLink"
            name="npaLink"
            label="Ссылка на НПА"
            multiline
            rows={8}
        />,
        <TextField
            key="abbreviations"
            margin="normal"
            value={entity.abbreviations || defaultAbbreviations}
            onChange={(e) => setValue("abbreviations", e.target.value || defaultAbbreviations)}
            fullWidth
            id="abbreviations"
            name="abbreviations"
            label="Сокращения"
            multiline
            rows={20}
        />,
        <Box key="questionnairesBox" sx={{mt: 2, mb: 2}}>
            <Typography variant="subtitle1" gutterBottom>
                Опросы (Вопросники)
            </Typography>
            <Box sx={{mb: 1}}>
                <Button
                    variant="outlined"
                    onClick={loadQuestionnairesByFilters}
                    disabled={!entity.assessmentDate || !entity.filialId || loadingQuestionnaires}
                    size="small"
                    sx={{mr: 1}}
                >
                    {loadingQuestionnaires ? "Загрузка..." : "Найти опросы по параметрам"}
                </Button>
                {selectedQuestionnaires.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        Выбрано опросов: {selectedQuestionnaires.length}
                    </Typography>
                )}
            </Box>
            <Autocomplete
                multiple
                options={allQuestionnaireOptions}
                value={selectedQuestionnaires}
                onChange={(event, newValue) => {
                    console.log("Autocomplete onChange:", { newValue, event });
                    // Используем функциональное обновление для надежности
                    setSelectedQuestionnaires(prev => {
                        console.log("Updating selectedQuestionnaires:", { prev, newValue });
                        return newValue || [];
                    });
                    const ids = (newValue || []).map(q => q.id);
                    setValue("questionnaireIds", ids.length > 0 ? ids : null);
                }}
                getOptionLabel={(option) => {
                    if (!option || !option.id) return '';
                    return getQuestionnaireLabel(option);
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return Number(option.id) === Number(value.id);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={getQuestionnaireLabel(option)}
                            {...getTagProps({index})}
                            key={option.id}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Выберите опросы"
                        placeholder="Начните вводить или используйте кнопку 'Найти опросы'"
                        helperText="Опросы будут автоматически отфильтрованы по году из даты оценки и филиалу"
                    />
                )}
                loading={loadingQuestionnaires}
                fullWidth
            />
            {!entity.assessmentDate || !entity.filialId ? (
                <Alert severity="info" sx={{mt: 1}}>
                    Укажите дату оценки и филиал, чтобы найти опросы
                </Alert>
            ) : null}
        </Box>,
        <TextField
            key="introduction"
            margin="normal"
            value={entity.introduction || ""}
            onChange={(e) => setValue("introduction", e.target.value || null)}
            fullWidth
            id="introduction"
            name="introduction"
            label="Введение"
            multiline
            rows={24}
        />,
        <TextField
            key="generalInformation"
            margin="normal"
            value={entity.generalInformation || ""}
            onChange={(e) => setValue("generalInformation", e.target.value || null)}
            fullWidth
            id="generalInformation"
            name="generalInformation"
            label="Общая информация"
            multiline
            rows={24}
        />,
        <TextField
            key="toolsDescription"
            margin="normal"
            value={entity.toolsDescription || ""}
            onChange={(e) => setValue("toolsDescription", e.target.value || null)}
            fullWidth
            id="toolsDescription"
            name="toolsDescription"
            label="Описание инструментов"
            multiline
            rows={24}
        />,
        <TextField
            key="results"
            margin="normal"
            value={entity.results || ""}
            onChange={(e) => setValue("results", e.target.value || null)}
            fullWidth
            id="results"
            name="results"
            label="Результаты"
            multiline
            rows={24}
        />,
        <TextField
            key="conclusion"
            margin="normal"
            value={entity.conclusion || ""}
            onChange={(e) => setValue("conclusion", e.target.value || null)}
            fullWidth
            id="conclusion"
            name="conclusion"
            label="Заключение"
            multiline
            rows={24}
        />,
        <TextField
            key="recommendations"
            margin="normal"
            value={entity.recommendations || ""}
            onChange={(e) => setValue("recommendations", e.target.value || null)}
            fullWidth
            id="recommendations"
            name="recommendations"
            label="Рекомендации"
            multiline
            rows={24}
        />,
        <Box key="pdfPreview" sx={{mt: 2}}>
            {pdfDataUrl && !pdfLoading ? (
                <iframe
                    src={pdfDataUrl}
                    style={{width: "100%", minHeight: "900px", border: "1px solid #ddd"}}
                    title="Report PDF preview"
                />
            ) : (
                <Box sx={{width: "100%", minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <CircularProgress size={28} />
                </Box>
            )}
        </Box>,
        <Box key="laboratoriesBox" sx={{mt: 2}}>
            <Typography variant="subtitle1" gutterBottom>
                Лаборатории (JSON)
            </Typography>
            <TextField
                margin="normal"
                value={formatJSON(entity.laboratories)}
                onChange={(e) => setValue("laboratories", parseJSON(e.target.value))}
                fullWidth
                id="laboratories"
                name="laboratories"
                label="Лаборатории (JSON формат)"
                multiline
                rows={6}
                helperText={`Введите JSON массив или объект. Например: [{"id": 1, "name": "Лаборатория №1"}]`}
                sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
            />
        </Box>,
        <Box key="expertsBox" sx={{mt: 2, mb: 2}}>
            <Typography variant="subtitle1" gutterBottom>
                Эксперты
            </Typography>
            <Autocomplete
                multiple
                options={employees}
                value={selectedExperts}
                onChange={(event, newValue) => {
                    setSelectedExperts(newValue || []);
                    // Сохраняем в формате JSON массива с id и name
                    const expertsJson = (newValue || []).map(emp => ({
                        id: emp.id,
                        name: emp.fullName || emp.name || `ID: ${emp.id}`
                    }));
                    setValue("experts", expertsJson.length > 0 ? expertsJson : null);
                }}
                getOptionLabel={(option) => {
                    if (!option) return '';
                    return option.fullName || option.name || `ID: ${option.id}`;
                }}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return Number(option.id) === Number(value.id);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            label={option.fullName || option.name || `ID: ${option.id}`}
                            {...getTagProps({index})}
                            key={option.id}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Выберите экспертов"
                        placeholder="Начните вводить имя эксперта"
                        helperText="Выберите пользователей из списка сотрудников"
                    />
                )}
                fullWidth
            />
        </Box>
    ], [entity, availableQuestionnaires, selectedQuestionnaires, loadingQuestionnaires, loadQuestionnairesByFilters, getQuestionnaireLabel, formatDate, formatDateISO, setValue, formatJSON, parseJSON, defaultNpaLink, defaultAbbreviations, allQuestionnaireOptions]);

    const editFields = useMemo(() => {
        return [
            <TextField
                key="id"
                margin="normal"
                value={entity.id || ""}
                fullWidth
                id="id"
                name="id"
                label="ID"
                disabled
            />,
            <TextField
                key="createdAt"
                margin="normal"
                type="datetime-local"
                value={formatDate(entity.createdAt)}
                onChange={(e) => setValue("createdAt", formatDateISO(e.target.value))}
                fullWidth
                id="createdAt"
                name="createdAt"
                label="Дата создания"
                InputLabelProps={{shrink: true}}
                disabled
            />,
            ...createFields // Все поля из createFields
        ];
    }, [entity, createFields, selectedQuestionnaires, availableQuestionnaires, loadingQuestionnaires]);

    const detailsFields = useMemo(() => {
        return [
            <TextField
                key="id"
                margin="normal"
                value={entity.id || ""}
                fullWidth
                id="id"
                name="id"
                label="ID"
                disabled
            />,
            <TextField
                key="createdAt"
                margin="normal"
                value={entity.createdAt ? new Date(entity.createdAt).toLocaleString('ru-RU') : "-"}
                fullWidth
                id="createdAt"
                name="createdAt"
                label="Дата создания"
                disabled
            />,
            <TextField
                key="assessmentDate"
                margin="normal"
                value={entity.assessmentDate ? new Date(entity.assessmentDate).toLocaleString('ru-RU') : "-"}
                fullWidth
                id="assessmentDate"
                name="assessmentDate"
                label="Дата оценки"
                disabled
            />,
            <TextField
                key="filialName"
                margin="normal"
                value={entity.filial?.name || (entity.filialId ? `ID: ${entity.filialId}` : "-")}
                fullWidth
                id="filialName"
                name="filialName"
                label="Филиал"
                disabled
            />,
            <TextField
                key="filialDivisionName"
                margin="normal"
                value={entity.filialDivision?.name || (entity.filialDivisionId ? `ID: ${entity.filialDivisionId}` : "-")}
                fullWidth
                id="filialDivisionName"
                name="filialDivisionName"
                label="Подразделение филиала"
                disabled
            />,
            <Box key="questionnairesBox" sx={{mt: 2, mb: 2}}>
                <Typography variant="subtitle1" gutterBottom>
                    Связанные опросы (Вопросники)
                </Typography>
                {selectedQuestionnaires.length > 0 ? (
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                        {selectedQuestionnaires.map((questionnaire) => (
                            <Chip
                                key={questionnaire.id}
                                label={getQuestionnaireLabel(questionnaire)}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Нет связанных опросов
                    </Typography>
                )}
            </Box>,
            <TextField
                key="npaLink"
                margin="normal"
                value={entity.npaLink || defaultNpaLink}
                fullWidth
                id="npaLink"
                name="npaLink"
                label="Ссылка на НПА"
                multiline
                rows={8}
                disabled
            />,
            <TextField
                key="abbreviations"
                margin="normal"
                value={entity.abbreviations || defaultAbbreviations}
                fullWidth
                id="abbreviations"
                name="abbreviations"
                label="Сокращения"
                multiline
                rows={20}
                disabled
            />,
            <TextField
                key="introduction"
                margin="normal"
                value={entity.introduction || "-"}
                fullWidth
                id="introduction"
                name="introduction"
                label="Введение"
                multiline
                rows={4}
                disabled
            />,
            <TextField
                key="generalInformation"
                margin="normal"
                value={entity.generalInformation || "-"}
                fullWidth
                id="generalInformation"
                name="generalInformation"
                label="Общая информация"
                multiline
                rows={4}
                disabled
            />,
            <TextField
                key="toolsDescription"
                margin="normal"
                value={entity.toolsDescription || "-"}
                fullWidth
                id="toolsDescription"
                name="toolsDescription"
                label="Описание инструментов"
                multiline
                rows={4}
                disabled
            />,
            <TextField
                key="results"
                margin="normal"
                value={entity.results || "-"}
                fullWidth
                id="results"
                name="results"
                label="Результаты"
                multiline
                rows={6}
                disabled
            />,
            <TextField
                key="conclusion"
                margin="normal"
                value={entity.conclusion || "-"}
                fullWidth
                id="conclusion"
                name="conclusion"
                label="Заключение"
                multiline
                rows={4}
                disabled
            />,
            <TextField
                key="recommendations"
                margin="normal"
                value={entity.recommendations || "-"}
                fullWidth
                id="recommendations"
                name="recommendations"
                label="Рекомендации"
                multiline
                rows={4}
                disabled
            />,
            <Box key="laboratoriesBox" sx={{mt: 2}}>
                <Typography variant="subtitle1" gutterBottom>
                    Лаборатории (JSON)
                </Typography>
                <Paper sx={{p: 2, bgcolor: '#f5f5f5', maxHeight: '300px', overflow: 'auto'}}>
                    <pre style={{margin: 0, fontSize: '0.875rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                        {formatJSON(entity.laboratories) || "Нет данных"}
                    </pre>
                </Paper>
            </Box>,
            <Box key="expertsBox" sx={{mt: 2, mb: 2}}>
                <Typography variant="subtitle1" gutterBottom>
                    Эксперты
                </Typography>
                {selectedExperts.length > 0 ? (
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                        {selectedExperts.map((expert) => (
                            <Chip
                                key={expert.id}
                                label={expert.fullName || expert.name || `ID: ${expert.id}`}
                                color="primary"
                                variant="outlined"
                            />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        Нет выбранных экспертов
                    </Typography>
                )}
            </Box>
        ];
    }, [entity, selectedQuestionnaires, selectedExperts]);

    const removeFields = useMemo(() => {
        return [
            <TextField
                key="id"
                margin="normal"
                value={entity.id || ""}
                fullWidth
                id="id"
                name="id"
                label="ID"
                disabled
            />,
            <TextField
                key="assessmentDate"
                margin="normal"
                value={entity.assessmentDate ? new Date(entity.assessmentDate).toLocaleDateString('ru-RU') : "-"}
                fullWidth
                id="assessmentDate"
                name="assessmentDate"
                label="Дата оценки"
                disabled
            />,
            <TextField
                key="filialName"
                margin="normal"
                value={entity.filial?.name || (entity.filialId ? `ID: ${entity.filialId}` : "-")}
                fullWidth
                id="filialName"
                name="filialName"
                label="Филиал"
                disabled
            />,
            <TextField
                key="filialDivisionName"
                margin="normal"
                value={entity.filialDivision?.name || (entity.filialDivisionId ? `ID: ${entity.filialDivisionId}` : "-")}
                fullWidth
                id="filialDivisionName"
                name="filialDivisionName"
                label="Подразделение филиала"
                disabled
            />
        ];
    }, [entity]);

    return (
        <EntityCRUDTable
            entityType="report"
            columns={columns}
            filterFields={filterFields}
            entity={entity}
            setEntity={setEntity}
            setValue={setValue}
            clearEntity={clearEntity}
            createFields={createFields}
            editFields={editFields}
            detailsFields={detailsFields}
            removeFields={removeFields}
            onAfterCreate={async (created, submittedEntity) => {
                // Автопревью после сохранения (как в протоколах) — не скачиваем файл.
                const newId = created?.id;
                if (!newId) {
                    await generatePdfPreview(submittedEntity);
                    return;
                }
                try {
                    const fresh = await reportCRUD.get(newId);
                    await generatePdfPreview(fresh || submittedEntity);
                } catch (e) {
                    await generatePdfPreview(submittedEntity);
                }
            }}
            onAfterEdit={async (_, submittedEntity) => {
                const id = submittedEntity?.id;
                if (!id) {
                    await generatePdfPreview(submittedEntity);
                    return;
                }
                try {
                    const fresh = await reportCRUD.get(id);
                    await generatePdfPreview(fresh || submittedEntity);
                } catch (e) {
                    await generatePdfPreview(submittedEntity);
                }
            }}
            createEntityTitle={t("report.create") || "Добавление отчета"}
            editEntityTitle={t("report.edit") || "Редактирование отчета"}
            detailsEntityTitle={t("report.details") || "Просмотр отчета"}
            removeEntityTitle={t("report.remove") || "Удаление отчета"}
            {...props}
        />
    );
}
